import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MailCheck } from "lucide-react";
import { api } from "../api";
import { Button, Field } from "../ui";

/**
 * Self-serve "forgot password" request. Always lands on the same
 * confirmation, whether or not the address matched an account — the
 * server is enumeration-safe and so is the copy here.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-ink-800">Reset your password</h1>
          <p className="mt-1.5 text-ink-500 font-light">
            We'll email you a one-time link to choose a new one.
          </p>
        </div>
        <div className="card">
          {sent ? (
            <div className="text-center py-4">
              <MailCheck size={28} className="mx-auto text-brand-700 mb-3" />
              <p className="text-sm text-ink-700 mb-1.5 font-medium">
                Check your inbox
              </p>
              <p className="text-sm text-ink-500">
                If <strong>{email}</strong> has an account, a reset link is on
                its way. It works once and expires in an hour. Don't forget
                the spam folder.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-1">
              <Field label="Email">
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                  />
                  <input
                    className="input pl-9"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="you@example.com"
                  />
                </div>
              </Field>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                className="w-full mt-2"
                loading={busy}
                disabled={busy || !email.trim()}
              >
                {busy ? "Sending…" : "Email me a reset link"}
                {!busy && <ArrowRight size={16} />}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-ink-500">
          Remembered it?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Back to sign in
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
